import { TranslateService } from '@ngx-translate/core';
import * as Papa from 'papaparse';

/**
 * The sample item CSV, built in the user's own app language (card #600, piece
 * 2 of #587's plan). One list -- i18n key -> the same two example values in
 * every language -- shared by every screen that offers the "Download sample
 * CSV" link, so the file can never drift between them.
 *
 * The i18n key for a column that also has an entry in
 * HEADER_ALIASES_BY_LANGUAGE (csv-header-mapping.ts) is deliberately the key
 * whose translated value IS that alias's first spelling (checked against
 * src/assets/i18n/{en,es,fr}.json at origin/develop 834bf2a) -- so the header
 * text this file writes is a spelling resolveHeaderMapping() already
 * recognises, and downloading + re-uploading the same file in es or fr
 * auto-maps every column, the same way an English file does today.
 *
 * merch-backend's own static public/assets/osmos_itemcsv.csv keeps its
 * "Additional values" column for older clients; it is left out here on
 * purpose -- it has no COMMON.* translation key and no destination field, so
 * it was never part of the real import contract.
 */
export interface ItemCsvTemplateColumn {
  /** the COMMON.<key> i18n key this column's header is translated from */
  i18nKey: string;
  /** the 2 example values, always written in English (card #587's plan) */
  values: [string, string];
}

export const ITEM_CSV_TEMPLATE_COLUMNS: ItemCsvTemplateColumn[] = [
  { i18nKey: 'Item Name', values: ['Name', 'Name 2'] },
  { i18nKey: 'Video Url', values: ['Item Video Url', 'Item Video Url'] },
  { i18nKey: 'Website Url', values: ['Item Website Url', 'Item Website Url'] },
  { i18nKey: 'Item Code', values: ['1', '1'] },
  { i18nKey: 'Unit Quantity', values: ['1', '1'] },
  { i18nKey: 'Units', values: ['Gram', 'Gram'] },
  { i18nKey: 'Description', values: ['Office Use Item', 'Office Use Item'] },
  { i18nKey: 'Deactivated', values: ['FALSE', 'FALSE'] },
  { i18nKey: 'Category', values: ['Furniture', 'Furniture'] },
  { i18nKey: 'Subcategory', values: ['Chairs', 'Chairs'] },
  { i18nKey: 'Images', values: ['Image', 'Image'] },
  { i18nKey: 'Item Discount', values: ['10', '10'] },
  { i18nKey: 'GST', values: ['10', '10'] },
  { i18nKey: 'Item Comments', values: ['Good Use', 'Good Use'] },
  { i18nKey: 'Fix Cost Per Unit', values: ['50', '50'] },
  { i18nKey: 'Sales Commission', values: ['10', '10'] },
  { i18nKey: 'Minimum Price', values: ['10', '10'] },
  { i18nKey: 'Price A', values: ['100', '100'] },
  { i18nKey: 'Price B', values: ['50', '50'] },
  { i18nKey: 'Price C', values: ['60', '60'] },
  { i18nKey: 'Price D', values: ['70', '70'] },
  { i18nKey: 'Tax Name', values: ['Sale tax', 'Income Tax'] },
  { i18nKey: 'Type', values: ['Service', 'Product'] },
  { i18nKey: 'Currency', values: ['USD', 'PKR'] },
];

/** The sample item CSV's text in the given language: translated header row, UTF-8 BOM, proper quoting (card #600). */
export function buildItemCsvTemplate(translate: TranslateService): string {
  const headers = ITEM_CSV_TEMPLATE_COLUMNS.map((column) => translate.instant('COMMON.' + column.i18nKey));
  const rows = [0, 1].map((rowIndex) => ITEM_CSV_TEMPLATE_COLUMNS.map((column) => column.values[rowIndex]));
  const BOM = '﻿';
  return BOM + Papa.unparse([headers, ...rows]);
}

/** Trigger the browser download of the sample item CSV (same Blob pattern as reporting-section.component.ts). */
export function downloadItemCsvTemplate(translate: TranslateService): void {
  const blob = new Blob([buildItemCsvTemplate(translate)], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'osmos_itemcsv.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
